import {
  ArrowDownTrayIcon,
  BanknotesIcon,
  ChevronRightIcon,
  PencilSquareIcon,
  TrashIcon,
} from '@heroicons/react/24/outline'
import { LayoutLoading } from 'components/LayoutLoading'
import { sumBy, totalsMoneyPlain, TotalsRow } from 'components/TableTotals'
import { AccountType, userHasAccountType } from 'config'
import { useSharedLoan } from 'context/SharedLoanContext'
import { Fragment, useEffect, useMemo, useState } from 'react'
import { useSelector } from 'react-redux'
import { toast } from 'react-toastify'
import {
  createServicingParticipation,
  deleteParticipationFunding,
  deleteServicingParticipation,
  generateServicingParticipation,
  getParticipationFunding,
  getServicingParticipation,
  resetParticipationAndUpb,
  restoreUpbFromReset,
  updateServicingParticipation,
} from 'services'
import { Button, Tooltip } from 'stories/components'
import { confirm, formatDate, formatTime, getPrice2decimal, renderHeader, sortByKey } from 'utils'
import { setLoanNumber } from 'utils/setLoanNumber'

import { AddParticipation } from './AddParticipation'
import { AddParticipationWizard } from './AddParticipationWizard'
import { FundingDebitModal } from './FundingDebitModal'
import type { ILenderParticipation, IParticipant } from './types'

/**
 * The participant's share of the loan, as the backend computes it: outstanding capital over the
 * loan's UPB. Trailing zeros are dropped but small slices keep their precision, so a 0.0625% strip
 * doesn't read as 0.06%. Blank when the balance couldn't be read.
 */
const formatOwnershipPct = (pct?: number | null) => {
  if (pct === null || pct === undefined || isNaN(Number(pct))) return '-'
  return `${parseFloat(Number(pct).toFixed(4))}%`
}

export default function ServicingParticipation() {
  const shared = useSharedLoan()
  const isShared = !!shared?.readOnly
  const [loading, setLoading] = useState(false)
  const [isOpen, setIsOpen] = useState(false)
  const [wizardOpen, setWizardOpen] = useState(false)
  const [data, setData] = useState<IParticipant[]>([])
  const [fundings, setFundings] = useState<any[]>([])
  const [selectedIndex, setSelectedIndex] = useState(-1)
  const [editingData, setEditingData] = useState<IParticipant | null>(null)
  const [interestRate, setInterestRate] = useState('')
  // The loan's lender when they hold part of it without a participation of their own, so the add
  // wizard can start their allocation from the balance they already hold.
  const [lender, setLender] = useState<ILenderParticipation | null>(null)
  const [byteproFileName, setByteproFileName] = useState(0)
  // A standing "Reset UPB" baseline. While it is set, the loan's balance reads that much lower
  // everywhere, so the screen surfaces it and offers to put it back.
  const [upbZeroOffset, setUpbZeroOffset] = useState(0)
  const [generatingId, setGeneratingId] = useState<number | null>(null)
  const [debitFunding, setDebitFunding] = useState<any>(null)
  const [expandedFundingId, setExpandedFundingId] = useState<number | null>(null)
  const [filters, setFilters] = useState({
    orderBy: 'createdAt',
    orderDir: -1,
  })

  const auth = useSelector((state: any) => state.auth)
  const isAdmin = !isShared && userHasAccountType(auth?.profile, AccountType.ADMIN)

  useEffect(() => {
    if (isShared) {
      const res = shared.pageData as any
      if (res?.success) {
        setData(res.participants || [])
        setLender(res.lender || null)
        setInterestRate(String(parseFloat(Number(res.interestRate || 0).toFixed(3))))
        setByteproFileName(res.byteproFileName)
        setUpbZeroOffset(Number(res.upbZeroOffset || 0))
      }
      return
    }
    setLoanNumber()
    refetch()
  }, [isShared, shared?.pageKey, shared?.pageData])

  const refetch = () => {
    if (isShared) return
    setLoading(true)
    getServicingParticipation()
      .then((res) => {
        if (res.success) {
          setData(res.participants || [])
          setLender(res.lender || null)
          // Show the loan's note rate at full precision (up to 3 decimals, no forced
          // trailing zeros) — eighths like 7.125 / 8.046 were being rounded to 2 places.
          setInterestRate(String(parseFloat(Number(res.interestRate || 0).toFixed(3))))
          setByteproFileName(res.byteproFileName)
          setUpbZeroOffset(Number(res.upbZeroOffset || 0))
        }
      })
      .finally(() => setLoading(false))
    getParticipationFunding()
      .then((res) => setFundings(res?.funding || []))
      .catch(() => {})
  }

  const onDeleteFunding = async (id: number) => {
    const content = (
      <div className="mb-4 text-[18px]">
        Delete this funding? Each participant's balance will be reduced by their allocation.
      </div>
    )
    const result = await confirm(content)
    if (!result) return
    setLoading(true)
    try {
      await deleteParticipationFunding(id)
      toast('Funding deleted', { type: 'info' })
      refetch()
    } catch {
      toast('Failed to delete the funding.', { type: 'error' })
      setLoading(false)
    }
  }

  const onResetAll = async () => {
    if (!isAdmin) return
    const content = (
      <div className="mb-4 text-[18px]">
        <p className="font-semibold text-red-700 mb-2">Delete all funding & reset UPB to 0?</p>
        This deletes every funding record on this loan, zeroes each participant's balance and distribution
        history, and forces the loan's UPB to 0. The deleted records cannot be recovered; only the UPB
        can be restored afterwards.
      </div>
    )
    const result = await confirm(content)
    if (!result) return
    setLoading(true)
    try {
      await resetParticipationAndUpb()
      toast('All funding deleted and UPB reset to 0.', { type: 'info' })
      refetch()
    } catch {
      toast('Failed to reset investor allocations.', { type: 'error' })
      setLoading(false)
    }
  }

  const onRestoreUpb = async () => {
    if (!isAdmin) return
    const content = (
      <div className="mb-4 text-[18px]">
        <p className="font-semibold mb-2">Restore this loan's UPB?</p>
        The loan's balance is being held down by ${getPrice2decimal(upbZeroOffset, false, true)} from an
        earlier UPB reset. Clearing it lets the balance be computed from the loan's own principal, draws
        and payments again. Deleted funding records are not restored.
      </div>
    )
    const result = await confirm(content)
    if (!result) return
    setLoading(true)
    try {
      const res = await restoreUpbFromReset()
      toast(`UPB restored to ${getPrice2decimal(res?.currentUpb || 0, false, true)}.`, { type: 'info' })
      refetch()
    } catch {
      toast('Failed to restore the UPB.', { type: 'error' })
      setLoading(false)
    }
  }

  const onAdd = () => {
    setWizardOpen(true)
  }

  const onCloseModal = () => {
    setIsOpen(false)
    setEditingData(null)
  }

  const onEdit = (_id: number, index: number) => {
    setEditingData(sortedData[index])
    setIsOpen(true)
    setSelectedIndex(index)
  }

  const onSubmit = async (newData: Record<string, any>) => {
    setLoading(true)
    try {
      if (selectedIndex === -1) {
        await createServicingParticipation(newData)
        toast('Successfully created an investor', { type: 'info' })
      } else {
        const item = sortedData[selectedIndex]
        await updateServicingParticipation(item.id, newData)
        toast('Successfully updated an investor', { type: 'info' })
      }
      setIsOpen(false)
      setSelectedIndex(-1)
      refetch()
    } catch (error) {
      console.log('Failed to submit investor data', error)
      setLoading(false)
    }
  }

  const onDelete = async (id: number, index: number) => {
    const content = (
      <div className="mb-4 text-[18px]">
        Do you want to remove this participant?
        <br />
        No. {index + 1}
      </div>
    )
    const result = await confirm(content)
    if (!result) return
    setLoading(true)
    await deleteServicingParticipation(id)
    toast('Successfully deleted an investor', { type: 'info' })
    refetch()
  }

  const onGenerate = async (id: number) => {
    const loanNumber = setLoanNumber()
    setGeneratingId(id)
    try {
      await generateServicingParticipation(id, loanNumber, byteproFileName)
    } finally {
      setGeneratingId(null)
    }
  }

  const onSort = (key: string, dir: number) => {
    setFilters({
      orderBy: key,
      orderDir: dir,
    })
  }

  const sortedData = useMemo(() => {
    if (!data.length) return []
    return sortByKey([...data], filters.orderBy, filters.orderDir as 1 | -1)
  }, [data, filters.orderBy, filters.orderDir])

  // Show funding history newest-first by funding date; fall back to id (insertion order)
  // for same-date events so the ordering stays stable.
  const sortedFundings = useMemo(() => {
    return [...fundings].sort((a, b) => {
      const byDate = String(b.date || '').localeCompare(String(a.date || ''))
      return byDate !== 0 ? byDate : (b.id || 0) - (a.id || 0)
    })
  }, [fundings])

  const sortableHeaders = [
    { title: 'Investor Name', key: 'participantName' },
    { title: 'Rep Name', key: 'participantRepName' },
    { title: 'Amount', key: 'participantAmount' },
    { title: 'PCT Owned', key: 'ownershipPct' },
    { title: 'Servicing Fee', key: 'participantServicingFee' },
    { title: 'Principal Rate', key: 'principalRate' },
    { title: 'Interest Rate', key: 'participantInterestRate' },
    { title: 'Created Date', key: 'createdAt' },
  ]

  return (
    <div className="participation-Container">
      <div className="max-w-screen-2xl m-auto">
        <div className="relative bg-white">
          <h2 className="text-2xl flex items-center gap-1 font-bold mb-2">
            <span>Investors</span>
          </h2>
          <LayoutLoading show={loading || generatingId !== null} />
          <div className="flex items-center gap-4 mb-2">
            {!isShared && (
              <Button onClick={onAdd} className="px-[36px]">
                Add Funding
              </Button>
            )}
            <div className="text-sm text-gray-700">
              Loan Interest Rate: <span className="font-semibold">{interestRate}%</span>
            </div>
          </div>
          <div className="parties-container overflow-auto max-h-[70vh] mb-6 mt-6 shadow-md sm:rounded-lg">
            <table className="w-full text-sm text-left text-gray-900 dark:text-gray-400 pl-6">
              <thead className="text-xs text-gray-700 uppercase bg-gray-100 dark:bg-gray-700 dark:text-gray-400">
                <tr>
                  <th scope="col" className="px-6 py-3 sticky top-0 z-20 bg-gray-100 dark:bg-gray-700">
                    No
                  </th>
                  {sortableHeaders.map((header, idx) =>
                    renderHeader({
                      className: 'sticky top-0 z-20 bg-gray-100 dark:bg-gray-700',
                      title: header.title,
                      key: header.key,
                      index: idx,
                      onSort,
                      sortable: true,
                      sortOrder: filters.orderBy === header.key ? filters.orderDir : 0,
                    }),
                  )}
                  {!isShared && (
                    <th scope="col" className="px-6 py-3 sticky top-0 z-20 bg-gray-100 dark:bg-gray-700">
                      Action
                    </th>
                  )}
                </tr>
              </thead>
              <tbody className="text-[14.5px] text-gray-900">
                {sortedData.length === 0 && (
                  <tr>
                    <td colSpan={sortableHeaders.length + (isShared ? 1 : 2)} className="text-center py-4 text-gray-500">
                      No participants found
                    </td>
                  </tr>
                )}
                {sortedData.map((participant, index) => (
                  <tr key={participant.id} className={`border-b ${index % 2 ? 'bg-gray-50' : ''}`}>
                    <td className="px-6 py-3">{index + 1}</td>
                    <td className="px-2 py-3">
                      {participant.participantName}
                      {/*
                        The loan's own lender, holding whatever was never sold down. It is an
                        ordinary row in every other respect, but which one it is matters: it is the
                        only row whose balance the loan already carries, so it is said rather than
                        left to be inferred from the name matching the party screen.
                      */}
                      {participant.isLender && (
                        <span className="ml-1 text-xs text-gray-500 whitespace-nowrap">(Lender)</span>
                      )}
                    </td>
                    <td className="px-2 py-3">{participant.participantRepName}</td>
                    <td className="px-2 py-3">${getPrice2decimal(participant.participantAmount)}</td>
                    <td className="px-2 py-3">{formatOwnershipPct(participant.ownershipPct)}</td>
                    <td className="px-2 py-3">${getPrice2decimal(participant.participantServicingFee)}</td>
                    <td className="px-2 py-3">{participant.principalRate}%</td>
                    {/*
                      The rate this investor is paid at - what the distribution scales their share
                      of the interest by. A participation carrying no rate of its own is simply owed
                      the loan rate, so show that rather than a bare "%": every participation
                      converted from a prior servicer arrives in exactly that state.
                    */}
                    <td className="px-2 py-3">
                      {participant.participantInterestRate ? (
                        `${participant.participantInterestRate}%`
                      ) : (
                        <span className="text-gray-500" title="No rate of its own, so it is owed the loan rate">
                          {interestRate}%
                        </span>
                      )}
                      {participant.receivesInterestSpread && (
                        <span
                          className="ml-2 text-xs rounded bg-shade-blue/10 text-shade-blue px-1.5 py-0.5"
                          title="Also paid the interest left over once every other investor has been paid at their own rate"
                        >
                          + spread
                        </span>
                      )}
                    </td>
                    <td className="px-2 py-3 w-40">{formatTime(participant.createdAt)}</td>
                    {!isShared && (
                      <td className="px-5 py-3">
                        <div className="flex gap-1 items-center">
                          <Tooltip message="Generate Participation Agreement">
                            <span
                              className="text-shade-blue cursor-pointer hover-shadow1 p-1 rounded"
                              onClick={() => onGenerate(participant.id)}
                            >
                              <ArrowDownTrayIcon className="w-4 h-4" />
                            </span>
                          </Tooltip>
                          <span
                            className="text-shade-blue cursor-pointer hover-shadow1 p-1 rounded"
                            onClick={() => onEdit(participant.id, index)}
                          >
                            <PencilSquareIcon className="w-4 h-4" />
                          </span>
                          <span
                            className="text-red-800 cursor-pointer hover-shadow1 p-1 rounded"
                            onClick={() => onDelete(participant.id, index)}
                          >
                            <TrashIcon className="w-4 h-4" />
                          </span>
                        </div>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
              {sortedData.length > 0 && (
                <TotalsRow
                  sticky
                  tdClass="px-2 py-3"
                  cells={[
                    { value: 'Totals', className: 'px-6 py-3' },
                    '',
                    '',
                    totalsMoneyPlain(sumBy(sortedData, 'participantAmount')),
                    '',
                    totalsMoneyPlain(sumBy(sortedData, 'participantServicingFee')),
                    '',
                    '',
                    '',
                    ...(isShared ? [] : ['']),
                  ]}
                />
              )}
            </table>
          </div>

          {/*
            The lender holds whatever was never sold down and has no row in the grid above until a
            participation is written down for them, so without this the PCT Owned column reads as
            under-allocated with nothing to explain the gap.
          */}
          {lender && lender.participantAmount > 0 && (
            <div className="-mt-4 mb-6 text-sm text-gray-700">
              <span className="font-semibold">{lender.participantName}</span> (Lender) holds $
              {getPrice2decimal(lender.participantAmount)}
              {lender.ownershipPct ? ` (${formatOwnershipPct(lender.ownershipPct)})` : ''} of this loan that has not
              been sold down. It is implied from the loan's balance rather than a participation of their own; adding a
              participation for them writes it down as a row above.
            </div>
          )}

          {!isShared && upbZeroOffset > 0 && (
            <div className="mb-4 rounded border border-amber-300 bg-amber-50 px-4 py-3 flex items-center justify-between gap-4">
              <div className="text-sm text-amber-900">
                <span className="font-semibold">This loan's UPB is being held down.</span> A UPB reset is
                subtracting ${getPrice2decimal(upbZeroOffset, false, true)} from the calculated balance, so the
                servicing screens, statements and payoff all quote the reduced figure.
              </div>
              {isAdmin && (
                <button
                  type="button"
                  onClick={onRestoreUpb}
                  className="shrink-0 text-[13px] font-semibold text-amber-900 border border-amber-400 hover:bg-amber-100 rounded px-3 py-1.5"
                >
                  Restore UPB
                </button>
              )}
            </div>
          )}

          {!isShared && (
            <>
              <div className="flex items-center justify-between mb-2">
                <h3 className="text-lg font-bold">Funding History</h3>
                {isAdmin && (
                  <button
                    type="button"
                    onClick={onResetAll}
                    className="text-[13px] font-semibold text-red-700 border border-red-300 hover:bg-red-50 rounded px-3 py-1.5 flex items-center gap-1"
                  >
                    <TrashIcon className="w-4 h-4" />
                    Delete All Funding &amp; Reset UPB
                  </button>
                )}
              </div>
              <div className="parties-container overflow-auto max-h-[70vh] mb-6 shadow-md sm:rounded-lg">
            <table className="w-full text-sm text-left text-gray-900">
              <thead className="text-xs text-gray-700 uppercase bg-gray-100">
                <tr>
                  <th className="px-6 py-3 sticky top-0 z-20 bg-gray-100">No</th>
                  <th className="px-2 py-3 sticky top-0 z-20 bg-gray-100">Date</th>
                  <th className="px-2 py-3 sticky top-0 z-20 bg-gray-100">Amount</th>
                  <th className="px-2 py-3 sticky top-0 z-20 bg-gray-100">Investors</th>
                  <th className="px-2 py-3 sticky top-0 z-20 bg-gray-100">Trust Ledger</th>
                  <th className="px-6 py-3 sticky top-0 z-20 bg-gray-100">Action</th>
                </tr>
              </thead>
              <tbody className="text-[14.5px] text-gray-900">
                {fundings.length === 0 && (
                  <tr>
                    <td colSpan={6} className="text-center py-4 text-gray-500">
                      No funding yet
                    </td>
                  </tr>
                )}
                {sortedFundings.map((funding, index) => {
                  const allocations = funding.allocations || []
                  const expanded = expandedFundingId === funding.id
                  const toggle = () => setExpandedFundingId(expanded ? null : funding.id)
                  // A construction draw that was split across investors. The backend mirrors it in
                  // here rather than storing a funding record for it, so there is nothing to debit
                  // or delete: the draw itself is the record, on the Construction Reserve tab.
                  const isDraw = !!funding.derived
                  return (
                    <Fragment key={funding.id}>
                      <tr className={`border-b ${index % 2 ? 'bg-gray-50' : ''}`}>
                        <td className="px-6 py-3">{index + 1}</td>
                        <td className="px-2 py-3">
                          <div className="flex items-center gap-2 whitespace-nowrap">
                            {funding.date ? formatDate(funding.date) : ''}
                            {isDraw && (
                              <Tooltip message="Funded by a construction draw on the Construction Reserve tab">
                                <span className="rounded bg-shade-blue/10 px-1.5 py-0.5 text-[11px] font-semibold uppercase text-shade-blue">
                                  Draw
                                </span>
                              </Tooltip>
                            )}
                          </div>
                        </td>
                        <td className="px-2 py-3">${getPrice2decimal(funding.amount)}</td>
                        <td className="px-2 py-3">
                          <Tooltip message={expanded ? 'Hide transaction details' : 'View transaction details'}>
                            <button
                              type="button"
                              onClick={toggle}
                              className="flex items-center gap-1 text-shade-blue hover:underline cursor-pointer"
                            >
                              <ChevronRightIcon
                                className={`w-3.5 h-3.5 transition-transform ${expanded ? 'rotate-90' : ''}`}
                              />
                              {allocations.length}
                            </button>
                          </Tooltip>
                        </td>
                        <td className="px-2 py-3">{funding.recordedInTrustLedger ? 'Yes' : 'No'}</td>
                        <td className="px-6 py-3">
                          {/*
                            Nothing to act on for a draw. There is no funding record behind the row
                            to delete, and the money went to the borrower's contractor out of the
                            reserve, so there is no investor capital left to collect for it either.
                          */}
                          {isDraw ? (
                            <span className="text-xs text-gray-500 whitespace-nowrap">On the construction ledger</span>
                          ) : (
                            <div className="flex gap-1 items-center">
                              <Tooltip
                                message={funding.achRunId ? 'Investors debited — review / reverse' : 'Debit Investors'}
                              >
                                <span
                                  className={`cursor-pointer hover-shadow1 p-1 rounded ${
                                    funding.achRunId ? 'text-green-700' : 'text-shade-blue'
                                  }`}
                                  onClick={() => setDebitFunding(funding)}
                                >
                                  <BanknotesIcon className="w-4 h-4" />
                                </span>
                              </Tooltip>
                              <span
                                className="text-red-800 cursor-pointer hover-shadow1 p-1 rounded"
                                onClick={() => onDeleteFunding(funding.id)}
                              >
                                <TrashIcon className="w-4 h-4" />
                              </span>
                            </div>
                          )}
                        </td>
                      </tr>
                      {expanded && (
                        <tr className="border-b bg-blue-50/40">
                          <td colSpan={6} className="px-6 py-4">
                            <div className="text-xs font-semibold text-gray-600 uppercase mb-2">
                              Transaction Details
                            </div>
                            {allocations.length === 0 ? (
                              <div className="text-sm text-gray-500">No investor allocations recorded.</div>
                            ) : (
                              <table className="w-full max-w-2xl text-sm text-left text-gray-900 mb-2">
                                <thead className="text-xs text-gray-600 uppercase">
                                  <tr>
                                    <th className="px-3 py-1.5">No</th>
                                    <th className="px-3 py-1.5">Investor</th>
                                    <th className="px-3 py-1.5 text-right">Amount</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {allocations.map((alloc: any, aIdx: number) => (
                                    <tr key={aIdx} className="border-t border-gray-200">
                                      <td className="px-3 py-1.5">{aIdx + 1}</td>
                                      <td className="px-3 py-1.5">{alloc.participantName || '-'}</td>
                                      <td className="px-3 py-1.5 text-right">${getPrice2decimal(alloc.amount)}</td>
                                    </tr>
                                  ))}
                                  <tr className="border-t border-gray-300 font-semibold">
                                    <td className="px-3 py-1.5" colSpan={2}>
                                      Total
                                    </td>
                                    <td className="px-3 py-1.5 text-right">${getPrice2decimal(funding.amount)}</td>
                                  </tr>
                                </tbody>
                              </table>
                            )}
                            <div className="text-xs text-gray-500 flex flex-wrap gap-x-6 gap-y-1">
                              {isDraw && (
                                <span>
                                  Construction draw
                                  {funding.reference ? `: ${funding.reference}` : ''} — edit or delete it on the
                                  Construction Reserve tab
                                </span>
                              )}
                              {funding.createdBy && <span>Created by: {funding.createdBy}</span>}
                              {funding.createdAt && <span>Created: {formatTime(funding.createdAt)}</span>}
                              {funding.achRunId && <span>ACH Run: #{funding.achRunId}</span>}
                            </div>
                          </td>
                        </tr>
                      )}
                    </Fragment>
                  )
                })}
              </tbody>
              {sortedFundings.length > 0 && (
                <TotalsRow
                  sticky
                  tdClass="px-2 py-3"
                  cells={[
                    { value: 'Totals', className: 'px-6 py-3' },
                    '',
                    totalsMoneyPlain(sumBy(sortedFundings, 'amount')),
                    '',
                    '',
                    { value: '', className: 'px-6 py-3' },
                  ]}
                />
              )}
            </table>
          </div>
            </>
          )}

          {!isShared && isOpen && (
            <AddParticipation
              index={selectedIndex}
              item={editingData}
              interestRate={interestRate}
              onClose={onCloseModal}
              onUpdate={onSubmit}
              loading={loading}
            />
          )}

          {!isShared && debitFunding && (
            <FundingDebitModal
              funding={debitFunding}
              onClose={() => setDebitFunding(null)}
              onDone={refetch}
            />
          )}

          {!isShared && wizardOpen && (
            <AddParticipationWizard
              existing={data}
              lender={lender}
              interestRate={interestRate}
              loading={loading}
              onClose={() => setWizardOpen(false)}
              onCreated={() => {
                setWizardOpen(false)
                refetch()
              }}
            />
          )}
        </div>
      </div>
    </div>
  )
}
